import React, { useMemo } from "react";
import { Typography, InputNumber } from "antd";
import { SbTag, SbTooltip } from "uiCore";
import { getFilters, buildStandardFilterColumns } from "./RedeploymentCenterV2.utils";
import PersonAvailabilityButton from "./PersonAvailabilityButton";

const { Text } = Typography;

// Column builders for the two top-level tables — same split as
// useTalentMobilityManagerColumns: the page owns state and handlers, these
// hooks own presentation. Handlers passed in must be referentially stable
// (useCallback in the page) since the memoized columns close over them.

export const useRedeploymentRoleColumns = ({ roles, thresholds, onTargetPositionChange }) =>
  useMemo(
    () => [
      { title: "Role", dataIndex: "title", filters: getFilters(roles, "title"), filterSearch: true, onFilter: (v, r) => r.title === v },
      ...buildStandardFilterColumns(roles),
      {
        // Read-only — current headcount is real data (counted from the
        // employee/JD assignments), unlike Target Positions which has no
        // source yet and stays editable.
        title: "Current Positions",
        dataIndex: "currentPositionCount",
        align: "center",
        sorter: (a, b) => a.currentPositionCount - b.currentPositionCount,
        // align: "center" only centers the body cells — a sortable header's
        // title+icon lays out in antd's own flex box, untouched by align.
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
      },
      {
        title: (
          <SbTooltip content="Set below Current Positions to plan a reduction for this role — opens the reduction detail screen.">
            Target Positions
          </SbTooltip>
        ),
        dataIndex: "targetPositionCount",
        align: "center",
        sorter: (a, b) => a.targetPositionCount - b.targetPositionCount,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        // No `max` here on purpose — antd swallows out-of-range input
        // silently, which would block typing "12" past an already-valid "1".
        // The page enforces the Current Positions ceiling itself instead and
        // explains the rejection with a toast. Commits on every change
        // (typed or stepped) so the ▲▼ stepper buttons update in real time —
        // see onTargetPositionChange for the reduction-only validation.
        render: (value, record) => (
          <InputNumber
            size="small"
            min={0}
            value={value}
            onClick={(event) => event.stopPropagation()}
            onChange={(nextValue) => onTargetPositionChange(record, nextValue)}
          />
        ),
      },
      {
        title: "Status",
        key: "reductionStatus",
        align: "center",
        render: (_, record) =>
          record.targetPositionCount < record.currentPositionCount ? (
            <SbTag color="error">Reducing</SbTag>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: `Matches (>${thresholds.green}%)`,
        dataIndex: "matchCount",
        align: "center",
        sorter: (a, b) => a.matchCount - b.matchCount,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        render: (count) => (count > 0 ? <SbTag color="success">{count}</SbTag> : <Text type="secondary">0</Text>),
      },
    ],
    [roles, thresholds, onTargetPositionChange]
  );

export const useRedeploymentPeopleColumns = ({ people }) =>
  useMemo(
    () => [
      {
        title: "Employee ID",
        dataIndex: "employeeId",
        filters: getFilters(people, "employeeId"),
        filterSearch: true,
        onFilter: (v, r) => r.employeeId === v,
        render: (v) => v || "—",
      },
      { title: "Person", dataIndex: "name", filters: getFilters(people, "name"), filterSearch: true, onFilter: (v, r) => r.name === v },
      {
        title: "Current Role",
        dataIndex: "currentRoleTitle",
        filters: getFilters(people, "currentRoleTitle"),
        filterSearch: true,
        onFilter: (v, r) => r.currentRoleTitle === v,
      },
      ...buildStandardFilterColumns(people),
      {
        title: "Potential Next Roles",
        key: "nextRoleCount",
        align: "center",
        // Mock rows count their precomputed array; API rows show the
        // listing's next_roles_count (their roles themselves load lazily on
        // expand). "—" only when neither is known.
        render: (_, record) => (record.nextRoles ? record.nextRoles.length : record.nextRolesCount ?? "—"),
      },
      {
        title: "",
        key: "availabilityAction",
        align: "center",
        // With tableLayout="fixed" (see RedeploymentCenterV2.jsx), `width`
        // is an exact, non-negotiable box — at 190, "Shortlist for
        // Redeployment"'s own rendered label barely leaves the default 16px
        // cell padding any room, so the button visually overflows its
        // padding box and sits flush against the cell's (here, the table's
        // own) right edge. 200 gives the longer label real breathing room on
        // both sides at size="small".
        width: 200,
        // Row-level shortcut for the same toggle the expanded next-roles
        // panel used to offer inline — lets a manager shortlist/revert
        // without expanding the row. PersonAvailabilityButton stops its own
        // click propagation so it doesn't also toggle the row's expand state
        // (see RedeploymentCenterV2.jsx's onRow).
        render: (_, record) => <PersonAvailabilityButton person={record} />,
      },
    ],
    [people]
  );
